import axios from 'axios';
import { safeStringify } from '../services/apiClient';
import {
  getLocalItem,
  getSessionItem,
  removeLocalItem,
  removeSessionItem,
  setLocalItem,
  setSessionItem,
} from '../utils/safeStorage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const axiosClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

const HOST_ACCESS_KEY = 'hostAccessToken';
const HOST_REFRESH_KEY = 'hostRefreshToken';
const isDev = process.env.NODE_ENV !== 'production';

const logAuth = (label, payload) => {
  if (!isDev || typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem('debugHostAuth') !== '1') return;
  } catch {
    return;
  }
  // eslint-disable-next-line no-console
  console.log(`[host-auth] ${label}`, payload);
};

const networkErrorMessage = () =>
  `Unable to reach the backend at ${API_BASE}. Please ensure the API server is running and reachable.`;

const normalizeNetworkError = (err) => {
  if (err instanceof TypeError && /fetch|network/i.test(err.message)) {
    const error = new Error(networkErrorMessage());
    error.code = 'NETWORK_ERROR';
    return error;
  }
  return err;
};

const emitHostAuthEvent = (name, detail) => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // ignore event dispatch failures
  }
};

const safeParse = (text) => {
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const handle = async (res) => {
  const text = await res.text();
  const data = safeParse(text);
  if (!res.ok) {
    const error = new Error(data?.message || 'Request failed');
    error.status = res.status;
    if (res.status === 401) error.code = 'HOST_AUTH_EXPIRED';
    throw error;
  }
  return data;
};

const storeHostToken = (token) => {
  if (!token) return;
  setSessionItem(HOST_ACCESS_KEY, token);
  setLocalItem(HOST_ACCESS_KEY, token);
  emitHostAuthEvent('host-auth-updated', { token });
};

const storeHostRefreshToken = (token) => {
  if (!token) return;
  setSessionItem(HOST_REFRESH_KEY, token);
  setLocalItem(HOST_REFRESH_KEY, token);
};

const clearHostToken = () => {
  removeSessionItem(HOST_ACCESS_KEY);
  removeLocalItem(HOST_ACCESS_KEY);
  removeSessionItem(HOST_REFRESH_KEY);
  removeLocalItem(HOST_REFRESH_KEY);
  emitHostAuthEvent('host-auth-expired');
};

const getStoredRefreshToken = () =>
  getSessionItem(HOST_REFRESH_KEY) || getLocalItem(HOST_REFRESH_KEY) || '';

let refreshAccessInFlight = null;
let refreshDataInFlight = null;
const refreshHostAccessToken = async () => {
  if (refreshAccessInFlight) return refreshAccessInFlight;
  refreshAccessInFlight = (async () => {
    try {
      const refreshToken = getStoredRefreshToken();
      const body = refreshToken ? safeStringify({ refreshToken }) : undefined;
      const headers = body ? { 'Content-Type': 'application/json' } : undefined;
      logAuth('refresh_start', { hasRefreshToken: !!refreshToken });
      const res = await fetch(`${API_BASE}/api/auth/host/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body,
      });
      if (!res.ok) {
        logAuth('refresh_failed', { status: res.status });
        return null;
      }
      const text = await res.text();
      const data = safeParse(text);
      const nextToken = data?.accessToken || data?.token;
      if (nextToken) storeHostToken(nextToken);
      if (data?.refreshToken) storeHostRefreshToken(data.refreshToken);
      logAuth('refresh_success', { hasToken: !!nextToken });
      return nextToken || null;
    } catch (err) {
      logAuth('refresh_error', { message: err?.message });
      return null;
    }
  })();
  try {
    return await refreshAccessInFlight;
  } finally {
    refreshAccessInFlight = null;
  }
};

const hostFetch = async (url, options = {}, accessToken) => {
  const { __skipRefresh, ...fetchOptions } = options;
  const buildHeaders = (token) => ({
    ...(fetchOptions.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });
  const doFetch = (token) =>
    fetch(url, {
      ...fetchOptions,
      headers: buildHeaders(token),
      credentials: 'include',
    });

  let res;
  try {
    res = await doFetch(accessToken);
  } catch (err) {
    throw normalizeNetworkError(err);
  }
  if (!__skipRefresh && res.status === 401) {
    logAuth('request_401', { url });
    const refreshed = await refreshHostAccessToken();
    if (refreshed) {
      try {
        res = await doFetch(refreshed);
      } catch (err) {
        throw normalizeNetworkError(err);
      }
      if (res.status === 401) {
        logAuth('request_401_after_refresh', { url });
        clearHostToken();
      }
    } else {
      clearHostToken();
    }
  }
  return handle(res);
};

const axiosHandle = async (promise) => {
  try {
    const res = await promise;
    return res.data;
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Request failed';
    throw new Error(message);
  }
};

axiosClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (error.response?.status === 401 && !error.config?.__isRetryRequest) {
      const refreshed = await refreshHostAccessToken();
      if (refreshed) {
        const retryConfig = { ...error.config, __isRetryRequest: true };
        retryConfig.headers = { ...(retryConfig.headers || {}), Authorization: `Bearer ${refreshed}` };
        return axiosClient.request(retryConfig);
      }
      clearHostToken();
    }
    return Promise.reject(error);
  }
);

export const hostSignup = async (payload) => {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/auth/host/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: safeStringify(payload),
    });
  } catch (err) {
    throw normalizeNetworkError(err);
  }
  const data = await handle(res);
  if (data?.refreshToken) storeHostRefreshToken(data.refreshToken);
  return data;
};

export const hostLogin = async (payload) => {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/auth/host/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: safeStringify(payload),
    });
  } catch (err) {
    throw normalizeNetworkError(err);
  }
  const data = await handle(res);
  if (data?.refreshToken) storeHostRefreshToken(data.refreshToken);
  return data;
};

export const hostRefresh = async () => {
  if (refreshDataInFlight) return refreshDataInFlight;
  refreshDataInFlight = (async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) return null;
    const body = refreshToken ? safeStringify({ refreshToken }) : undefined;
    const headers = body ? { 'Content-Type': 'application/json' } : undefined;
    logAuth('refresh_start', { hasRefreshToken: !!refreshToken, source: 'hostRefresh' });
    let res;
    try {
      res = await fetch(`${API_BASE}/api/auth/host/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body,
      });
    } catch (err) {
      throw normalizeNetworkError(err);
    }
    const data = await handle(res);
    if (data?.refreshToken) storeHostRefreshToken(data.refreshToken);
    logAuth('refresh_success', { hasToken: !!data?.accessToken, source: 'hostRefresh' });
    return data;
  })();
  try {
    return await refreshDataInFlight;
  } finally {
    refreshDataInFlight = null;
  }
};

export const hostMe = async (accessToken, options = {}) => {
  return hostFetch(`${API_BASE}/api/auth/host/me`, { __skipRefresh: options.skipRefresh }, accessToken);
};

export const hostLogout = async (accessToken) => {
  await hostFetch(
    `${API_BASE}/api/auth/host/logout`,
    {
      method: 'POST',
    },
    accessToken
  );
  removeSessionItem(HOST_REFRESH_KEY);
  removeLocalItem(HOST_REFRESH_KEY);
};

export const hostSummary = async (accessToken) => {
  return hostFetch(`${API_BASE}/api/host/dashboard/summary`, {}, accessToken);
};

export const hostRoleSummary = async (accessToken) => {
  return hostFetch(`${API_BASE}/api/host/summary`, {}, accessToken);
};

export const hostListProperties = async (accessToken, params = {}) => {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (params.limit) query.set('limit', params.limit);
  if (params.q) query.set('q', params.q);
  if (params.status) query.set('status', params.status);
  return hostFetch(
    `${API_BASE}/api/host/properties${query.toString() ? `?${query}` : ''}`,
    {},
    accessToken
  );
};

export const hostCreateProperty = async (accessToken, body) => {
  return hostFetch(
    `${API_BASE}/api/host/properties`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify(body),
    },
    accessToken
  );
};

export const hostUpdateProperty = async (accessToken, id, body) => {
  return hostFetch(
    `${API_BASE}/api/host/properties/${id}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify(body),
    },
    accessToken
  );
};

export const hostDeleteProperty = async (accessToken, id) => {
  return hostFetch(
    `${API_BASE}/api/host/properties/${id}`,
    {
      method: 'DELETE',
    },
    accessToken
  );
};

export const hostGetProperty = async (accessToken, id) => {
  return hostFetch(`${API_BASE}/api/host/properties/${id}`, {}, accessToken);
};

export const hostGetPartialPaymentPolicy = async (accessToken) => {
  return hostFetch(`${API_BASE}/api/host/partial-payment/policy`, {}, accessToken);
};

export const hostGetPropertyPartialPayment = async (accessToken, id) => {
  return hostFetch(`${API_BASE}/api/host/properties/${id}/partial-payment`, {}, accessToken);
};

export const hostUpdatePropertyPartialPayment = async (accessToken, id, body) => {
  return hostFetch(
    `${API_BASE}/api/host/properties/${id}/partial-payment`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify(body),
    },
    accessToken
  );
};

export const hostSaveImages = async (accessToken, id, images) => {
  return hostFetch(
    `${API_BASE}/api/host/properties/${id}/images`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify({ images }),
    },
    accessToken
  );
};

export const hostPublishProperty = async (accessToken, id) => {
  return hostFetch(
    `${API_BASE}/api/host/properties/${id}/publish`,
    {
      method: 'POST',
    },
    accessToken
  );
};

export const hostSubmitProperty = async (accessToken, id) => {
  return hostFetch(
    `${API_BASE}/api/host/properties/${id}/submit`,
    {
      method: 'POST',
    },
    accessToken
  );
};

export const hostUpdatePricingRules = async (accessToken, id, body) => {
  return hostFetch(
    `${API_BASE}/api/host/properties/${id}/pricing-rules`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify(body),
    },
    accessToken
  );
};

export const hostUpdateAmenities = async (accessToken, id, amenities, extraNotes) => {
  return hostFetch(
    `${API_BASE}/api/host/properties/${id}/amenities`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify({ amenities, extra_notes: extraNotes || null }),
    },
    accessToken
  );
};

export const hostBookings = async (accessToken, params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  });
  const data = await hostFetch(
    `${API_BASE}/api/host/bookings${qs.toString() ? `?${qs}` : ''}`,
    {},
    accessToken
  );
  return data?.data || data?.items || data;
};

const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
};

export const hostPayouts = async (accessToken, params = {}) => {
  return hostFetch(`${API_BASE}/api/host/finance/payouts${buildQuery(params)}`, {}, accessToken);
};

export const hostTransactions = async (accessToken, params = {}) => {
  return hostFetch(`${API_BASE}/api/host/finance/statement${buildQuery(params)}`, {}, accessToken);
};

// Promotions & card offers
export const hostListPromotions = async (accessToken) => {
  return hostFetch(`${API_BASE}/api/host/promotions`, {}, accessToken);
};

export const hostSavePromotion = async (accessToken, payload) => {
  const method = payload.id ? 'PUT' : 'POST';
  const url = payload.id ? `${API_BASE}/api/host/promotions/${payload.id}` : `${API_BASE}/api/host/promotions`;
  return hostFetch(
    url,
    {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify(payload),
    },
    accessToken
  );
};

export const hostDeletePromotion = async (accessToken, id) => {
  return hostFetch(
    `${API_BASE}/api/host/promotions/${id}`,
    {
      method: 'DELETE',
    },
    accessToken
  );
};

export const hostTogglePromotion = async (accessToken, id, is_active) => {
  return hostFetch(
    `${API_BASE}/api/host/promotions/${id}/toggle`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify({ is_active }),
    },
    accessToken
  );
};

export const hostListCardOffers = async (accessToken) => {
  return hostFetch(`${API_BASE}/api/host/card-offers`, {}, accessToken);
};

export const hostSaveCardOffer = async (accessToken, payload) => {
  const method = payload.id ? 'PUT' : 'POST';
  const url = payload.id ? `${API_BASE}/api/host/card-offers/${payload.id}` : `${API_BASE}/api/host/card-offers`;
  return hostFetch(
    url,
    {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify(payload),
    },
    accessToken
  );
};

export const hostDeleteCardOffer = async (accessToken, id) => {
  return hostFetch(
    `${API_BASE}/api/host/card-offers/${id}`,
    {
      method: 'DELETE',
    },
    accessToken
  );
};

export const hostToggleCardOffer = async (accessToken, id, is_active) => {
  return hostFetch(
    `${API_BASE}/api/host/card-offers/${id}/toggle`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify({ is_active }),
    },
    accessToken
  );
};

// Pricing & deals (new)
export const hostPricingMeta = async (accessToken) => {
  return hostFetch(`${API_BASE}/api/host/pricing/meta`, {}, accessToken);
};

const withAuthJson = (accessToken, method, url, body) =>
  hostFetch(
    url,
    {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body !== undefined ? safeStringify(body) : undefined,
    },
    accessToken
  );

// Seasonal pricing
export const hostListSeasonalPricing = (accessToken, propertyId) =>
  hostFetch(
    `${API_BASE}/api/host/pricing/seasonal${propertyId ? `?propertyId=${propertyId}` : ''}`,
    {},
    accessToken
  );

export const hostSaveSeasonalPricing = (accessToken, payload) => {
  const method = payload.id ? 'PUT' : 'POST';
  const url = payload.id
    ? `${API_BASE}/api/host/pricing/seasonal/${payload.id}`
    : `${API_BASE}/api/host/pricing/seasonal`;
  return withAuthJson(accessToken, method, url, payload);
};

export const hostDeleteSeasonalPricing = (accessToken, id) =>
  hostFetch(
    `${API_BASE}/api/host/pricing/seasonal/${id}`,
    {
      method: 'DELETE',
    },
    accessToken
  );

// Coupons / promo codes
export const hostListCoupons = (accessToken, propertyId) =>
  hostFetch(
    `${API_BASE}/api/host/pricing/coupons${propertyId ? `?propertyId=${propertyId}` : ''}`,
    {},
    accessToken
  );

export const hostSaveCoupon = (accessToken, payload) => {
  const method = payload.id ? 'PUT' : 'POST';
  const url = payload.id
    ? `${API_BASE}/api/host/pricing/coupons/${payload.id}`
    : `${API_BASE}/api/host/pricing/coupons`;
  return withAuthJson(accessToken, method, url, payload);
};

export const hostDeleteCoupon = (accessToken, id) =>
  hostFetch(
    `${API_BASE}/api/host/pricing/coupons/${id}`,
    {
      method: 'DELETE',
    },
    accessToken
  );

export const hostUploadCouponImage = async (accessToken, file) => {
  const form = new FormData();
  form.append('image', file);
  return hostFetch(
    `${API_BASE}/api/host/pricing/coupons/upload-card`,
    {
      method: 'POST',
      body: form,
    },
    accessToken
  );
};

// Special offers
export const hostListOffers = (accessToken, propertyId) =>
  hostFetch(
    `${API_BASE}/api/host/pricing/offers${propertyId ? `?propertyId=${propertyId}` : ''}`,
    {},
    accessToken
  );

export const hostSaveOffer = (accessToken, payload) => {
  const method = payload.id ? 'PUT' : 'POST';
  const url = payload.id
    ? `${API_BASE}/api/host/pricing/offers/${payload.id}`
    : `${API_BASE}/api/host/pricing/offers`;
  return withAuthJson(accessToken, method, url, payload);
};

export const hostDeleteOffer = (accessToken, id) =>
  hostFetch(
    `${API_BASE}/api/host/pricing/offers/${id}`,
    {
      method: 'DELETE',
    },
    accessToken
  );

// Taxes & fees
export const hostListTaxesFees = (accessToken, propertyId) =>
  hostFetch(
    `${API_BASE}/api/host/pricing/taxes-fees${propertyId ? `?propertyId=${propertyId}` : ''}`,
    {},
    accessToken
  );

export const hostSaveTaxFee = (accessToken, payload) => {
  const method = payload.id ? 'PUT' : 'POST';
  const url = payload.id
    ? `${API_BASE}/api/host/pricing/taxes-fees/${payload.id}`
    : `${API_BASE}/api/host/pricing/taxes-fees`;
  return withAuthJson(accessToken, method, url, payload);
};

export const hostDeleteTaxFee = (accessToken, id) =>
  hostFetch(
    `${API_BASE}/api/host/pricing/taxes-fees/${id}`,
    {
      method: 'DELETE',
    },
    accessToken
  );

export const hostUpdateProfile = async (accessToken, payload) => {
  return axiosHandle(
    axiosClient.put('/api/host/profile', payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  );
};

export const hostGetBankAccount = async (accessToken) => {
  return hostFetch(`${API_BASE}/api/host/finance/bank-account`, {}, accessToken);
};

export const hostSaveBankAccount = async (accessToken, payload) => {
  return hostFetch(
    `${API_BASE}/api/host/finance/bank-account`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: safeStringify(payload),
    },
    accessToken
  );
};

export const hostProfile = async (accessToken) => {
  return axiosHandle(
    axiosClient.get('/api/host/profile', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  );
};

export const hostUploadProfileImage = async (accessToken, file) => {
  const form = new FormData();
  form.append('image', file);
  return axiosHandle(
    axiosClient.post('/api/host/profile/image', form, {
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'multipart/form-data' },
    })
  );
};

export const hostListKyc = async (accessToken) => {
  return axiosHandle(
    axiosClient.get('/api/host/kyc', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  );
};

export const hostUploadKyc = async (accessToken, payload) => {
  const form = new FormData();
  if (payload?.doc_type) form.append('doc_type', payload.doc_type);
  if (payload?.doc_number) form.append('doc_number', payload.doc_number);
  if (payload?.file) form.append('file', payload.file);
  return axiosHandle(
    axiosClient.post('/api/host/kyc', form, {
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'multipart/form-data' },
    })
  );
};

// Support
export const hostSupportTickets = async (accessToken) =>
  axiosHandle(
    axiosClient.get('/api/host/support/tickets', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  );

export const hostSupportTicket = async (accessToken, ticketId) =>
  axiosHandle(
    axiosClient.get(`/api/host/support/tickets/${ticketId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  );

export const hostSupportCreate = async (accessToken, payload) => {
  const form = new FormData();
  form.append('subject', payload.subject);
  form.append('category', payload.category);
  form.append('priority', payload.priority);
  form.append('message', payload.message);
  if (payload.attachment) form.append('attachment', payload.attachment);
  return axiosHandle(
    axiosClient.post('/api/host/support/tickets', form, {
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'multipart/form-data' },
    })
  );
};

export const hostSupportReply = async (accessToken, ticketId, payload) => {
  const form = new FormData();
  if (payload.message) form.append('message', payload.message);
  if (payload.attachment) form.append('attachment', payload.attachment);
  return axiosHandle(
    axiosClient.post(`/api/host/support/tickets/${ticketId}/message`, form, {
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'multipart/form-data' },
    })
  );
};


